import { useEffect, useState, useRef } from 'react';
import stickyHeaderService from '../services/StickyHeaderService';
import { DEFAULT_MFE_CONTEXT, CATEGORY_MFE_CONTEXT } from '../tools/constants';
import useIsScrolling from './useIsScrolling';
import useIsSsr from './useIsSsr';

/**
 * Hook for managing sticky header state across MFEs like SmallScreen and UnifiedHeader
 * Only activates sticky behavior when used in CategoryPage context
 *
 * @param {string} mfeContext - The MFE context ('categoryPage' or 'default')
 * @returns {Object} Sticky header state and methods
 */
const useStickyHeaderState = (mfeContext = DEFAULT_MFE_CONTEXT) => {
  const [state, setState] = useState({
    isNavbarVisible: true,
    isFiltersSticky: false,
    lastScrollDirection: null,
  });

  const isScrollingUp = useIsScrolling(['up']);
  const isScrollingDown = useIsScrolling(['down']);
  const subscriptionRef = useRef(null);
  const isInitializedRef = useRef(false);
  const isServerSide = useIsSsr();

  // Initialize subscription to service (client-side only)
  useEffect(() => {
    if (isServerSide || isInitializedRef.current) return undefined;

    // Subscribe to state changes from the service
    subscriptionRef.current = stickyHeaderService.subscribe((newState) => {
      setState(newState);
    });

    isInitializedRef.current = true;

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current();
        subscriptionRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, [isServerSide]);

  // Handle scroll direction changes (client-side only)
  useEffect(() => {
    if (isServerSide || !isInitializedRef.current) return;

    let direction = null;
    if (isScrollingUp) {
      direction = 'up';
    } else if (isScrollingDown) {
      direction = 'down';
    }

    if (direction) {
      // Only handle scroll direction if we're in CategoryPage context
      if (mfeContext === CATEGORY_MFE_CONTEXT) {
        stickyHeaderService.handleScrollDirection(direction);
      }
    }
  }, [isScrollingUp, isScrollingDown, isServerSide, mfeContext]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (subscriptionRef.current) {
      subscriptionRef.current();
      subscriptionRef.current = null;
    }
    isInitializedRef.current = false;
  }, []);

  return {
    isNavbarVisible: state.isNavbarVisible,
    isFiltersSticky: state.isFiltersSticky,
    lastScrollDirection: state.lastScrollDirection,
    // Methods for manual state control (only work in CategoryPage context)
    setIsNavbarVisible: (visible) => {
      if (mfeContext === CATEGORY_MFE_CONTEXT) {
        stickyHeaderService.updateState({ isNavbarVisible: visible }, 'useStickyHeaderState');
      }
    },
    setIsFiltersSticky: (sticky) => {
      if (mfeContext === CATEGORY_MFE_CONTEXT) {
        stickyHeaderService.updateState({ isFiltersSticky: sticky }, 'useStickyHeaderState');
      }
    },
    reset: () => stickyHeaderService.reset(),
    requestState: () => stickyHeaderService.getState(),
  };
};

export default useStickyHeaderState;
